import React, { useState, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import WhiteMainBackgroundWithBackButton from '../../../shared_components/WhiteMainBackgroundWithBackButton/WhiteMainBackgroundWithBackButton';
import SearchButton from '../../../shared_components/SearchButton/SearchButton';
import PaginationGroup from '../../../shared_components/PaginationGroup/PaginationGroup';
import Spinner from '../../../shared_components/Spinner/Spinner';
import SearchWizardService from '../../../services/SearchWizardService';
import UserData from '../../../services/UserData';
import AuthUtils from '../../../services/AuthUtils';
import TaskCard from './TaskCard/TaskCard';

import styles from './Tags.module.css';

function Tags() {
	const navigate = useNavigate();
	const userData = useContext(UserData);
	const [tableData, setTableData] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [hasSearched, setHasSearched] = useState(false);
	const [retryCount, setRetryCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);

	const CARDS_PER_PAGE = 9;

	// Memoize authentication status
	const isAuthenticated = useMemo(
		() => AuthUtils.isAuthenticated(userData),
		[userData]
	);

	const handleBackButton = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	// Transform API response to only store needed properties
	const transformTaskResults = useCallback((results, term) => {
		if (Array.isArray(results) && results.length > 0) {
			return results.map((task) => ({
				id: task.id, // Keep ID for stable key prop
				name: task.taskName || `Task ${task.id}`,
				tags: task.tags?.length ? task.tags : [term],
				teamwork: task.url,
			}));
		}
		return [];
	}, []);

	const handleSearch = useCallback(
		(term) => {
			setSearchTerm(term);

			if (!term.trim()) {
				setTableData([]);
				setHasSearched(false);
				setError(null);
				setLoading(false);
				setRetryCount(0);
				setCurrentPage(1);
				return;
			}

			if (!isAuthenticated) {
				setError('Please log in to search for tags');
				setLoading(false);
				setTableData([]);
				setHasSearched(false);
				navigate('/login');
				return;
			}

			// Clear previous results immediately when starting new search
			setTableData([]);
			setLoading(true);
			setError(null);
			setHasSearched(true);
			setCurrentPage(1);

			SearchWizardService.searchTasksByTag(term, userData.jwtToken, userData)
				.then((results) => {
					// Transform API response to only store needed data
					const formattedResults = transformTaskResults(results, term);
					setTableData(formattedResults);
					setRetryCount(0);
				})
				.catch((error) => {
					let errorMessage = error.message.includes('Server error')
						? retryCount >= 2
							? 'Sorry, our server is having issues. Please try again later or contact support.'
							: 'Sorry, something went wrong on our server. Please try again.'
						: error.message || 'Failed to search for tags';
					setError(errorMessage);
					setTableData([]);
					if (
						error.message.includes('Invalid JWT') ||
						error.message.includes('HTTP 401')
					) {
						navigate('/login');
						setRetryCount(0);
					}
				})
				.finally(() => {
					setLoading(false);
				});
		},
		[userData, navigate, retryCount, isAuthenticated, transformTaskResults]
	);

	const handleRetry = useCallback(() => {
		if (searchTerm.trim()) {
			setRetryCount((prev) => prev + 1);
			handleSearch(searchTerm);
		}
	}, [searchTerm, handleSearch]);

	// Pagination calculations
	const totalPages = Math.ceil(tableData.length / CARDS_PER_PAGE);
	const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
	const endIndex = startIndex + CARDS_PER_PAGE;
	const currentPageData = tableData.slice(startIndex, endIndex);

	const handlePageChange = useCallback((page) => {
		setCurrentPage(page);
	}, []);

	// Suggested tags for empty state
	const suggestedTags = [
		'Characters',
		'Walk Cycle',
		'Run Cycle',
		'Bg Office',
		'Building',
	];

	return (
		<WhiteMainBackgroundWithBackButton
			heading='Knowledge Center'
			backButtonAction={handleBackButton}>
			<div className={styles.container}>
				{/* <p className={styles.instructionText}>
          Search a tag related to the artwork you are looking for
        </p> */}

				<SearchButton
					placeholder='Search for tags'
					searchAction={handleSearch}
				/>

				{error && (
					<div className={styles.error}>
						{error}
						{error.includes('server') && retryCount < 3 && (
							<button onClick={handleRetry} className={styles.retryButton}>
								Retry
							</button>
						)}
					</div>
				)}

				{loading && (
					<div className={styles.loadingContainer}>
						<Spinner />
					</div>
				)}

				{!hasSearched ? (
					<div className={styles.emptyState}>
						<p>Enter a tag to find associated Teamwork tasks</p>
						<div className={styles.suggestedTags}>
							<p>Try searching for:</p>
							{suggestedTags.map((tag) => (
								<button
									key={tag}
									onClick={() => handleSearch(tag)}
									className={styles.tagSuggestion}>
									{tag}
								</button>
							))}
						</div>
					</div>
				) : tableData.length === 0 && !loading ? (
					<div className={styles.noResultsContainer}>
						<p className={styles.noResultsMessage}>
							No Teamwork tasks found for "{searchTerm}"
						</p>
						<p>Try another tag or check your spelling</p>
					</div>
				) : null}

				{hasSearched && tableData.length > 0 && (
					<>
						{/* <hr className={styles.separator} /> */}
						<div className={styles.cardsContainer}>
							{currentPageData.map((task) => (
								<TaskCard
									key={task.id || task.name}
									task={task}
									searchTerm={searchTerm}
								/>
							))}
						</div>

						<div className={styles.paginationContainer}>
							<PaginationGroup
								activePage={currentPage}
								numberOfPages={totalPages}
								updatePageClicked={handlePageChange}
							/>
						</div>
					</>
				)}
			</div>
		</WhiteMainBackgroundWithBackButton>
	);
}

export default React.memo(Tags);
